import { useState, useEffect, useCallback } from 'react';
import { commonCodeApi } from '../api/commonCodeApi';
import { getItem, removeItem, STORAGE_KEYS } from '@/shared/utils/storage';
import { useToast } from '@/shared/components/feedback/Toast/ToastContext';
import { ToastType } from '@/shared/components/feedback/Toast/Toast.types';
import {
    extractCodesFromResponse,
    extractGroupsFromResponse,
    calculateVisibleValueFields,
} from '../../shared/lib/commonCodeParserHelpers';
import { DASHBOARD_EVENTS, SYSTEM_TAB_IDS } from '../../shared/config/system.config';
import {
    CommonCodeFormItem,
    GroupFormData,
    createEmptyCodeItem,
    createEmptyGroupFormData,
    removeValueFieldAt,
    addValueField,
    validateForm,
} from '../utils/commonCodeCreateUtils';
import { TOAST_MESSAGES } from '../constants/commonCode.constants';

export const useCommonCodeCreate = () => {
    const { showToast } = useToast();

    const [grpCode] = useState<string | undefined>(() => {
        const savedGrpCode = getItem<string>('COMMON_CODE_EDIT_GRP_CODE');
        if (savedGrpCode) {
            removeItem('COMMON_CODE_EDIT_GRP_CODE');
            return savedGrpCode;
        }
        removeItem('COMMON_CODE_EDIT_MODE');
        return undefined;
    });

    const [formData, setFormData] = useState<GroupFormData>(createEmptyGroupFormData);
    const [commonCodes, setCommonCodes] = useState<CommonCodeFormItem[]>([createEmptyCodeItem()]);
    const [loading, setLoading] = useState(false);

    const isEditMode = !!grpCode;

    useEffect(() => {
        if (!grpCode) return;

        const fetchGroupData = async () => {
            setLoading(true);
            try {
                const response = await commonCodeApi.getCommonCode(grpCode);
                const codes = extractCodesFromResponse(response);
                const groups = extractGroupsFromResponse(response);
                const group = groups.find(g => g.grpCode === grpCode);

                if (group) {
                    setFormData({
                        grpCode: group.grpCode,
                        grpName: group.grpName,
                        grpVal: group.grpVal,
                        seqNo: group.seqNo,
                    });

                    if (codes.length > 0) {
                        const groupCodes = codes.filter(code => code.grpCode === grpCode);
                        setCommonCodes(groupCodes.map((code, index) => ({
                            id: `code-${Date.now()}-${index}`,
                            comCode: code.comCode,
                            comCdName: code.comCdName,
                            comSeqno: (code.comSeqno || code.seqNo)?.toString() || '',
                            seqNo: code.seqNo,
                            useYn: code.useYn,
                            val1: code.val1 || '',
                            val2: code.val2 || '',
                            val3: code.val3 || '',
                            val4: code.val4 || '',
                            val5: code.val5 || '',
                            visibleValueFields: calculateVisibleValueFields(code),
                        })));
                    }
                }
            } catch {
                // 데이터 로드 실패 시 조용히 처리
            } finally {
                setLoading(false);
            }
        };

        fetchGroupData();
    }, [grpCode]);

    const handleGroupChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    }, []);

    const handleCommonCodeChange = useCallback((
        id: string,
        e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
    ) => {
        const { name, value } = e.target;
        setCommonCodes(prev => prev.map(code =>
            code.id === id ? { ...code, [name]: value } : code
        ));
    }, []);

    const handleAddCommonCode = useCallback(() => {
        setCommonCodes(prev => [...prev, createEmptyCodeItem()]);
    }, []);

    const handleRemoveCommonCode = useCallback((id: string) => {
        setCommonCodes(prev => {
            if (prev.length === 1) {
                showToast(TOAST_MESSAGES.MIN_CODE_REQUIRED, { type: ToastType.WARNING });
                return prev;
            }
            return prev.filter(code => code.id !== id);
        });
    }, [showToast]);

    const handleRemoveValueField = useCallback((id: string, fieldIndex: number) => {
        setCommonCodes(prev => prev.map(code => {
            if (code.id !== id) return code;
            return { ...code, ...removeValueFieldAt(code, fieldIndex) };
        }));
    }, []);

    const handleAddValueField = useCallback((id: string) => {
        setCommonCodes(prev => prev.map(code => {
            if (code.id !== id) return code;
            return { ...code, ...addValueField(code) };;
        }));
    }, []);

    const handleReset = useCallback(() => {
        setCommonCodes([createEmptyCodeItem()]);
    }, []);

    const handleSubmit = useCallback(async (e: React.FormEvent) => {
        e.preventDefault();

        const errorMessage = validateForm(formData, commonCodes);
        if (errorMessage) {
            showToast(errorMessage, { type: ToastType.ERROR });
            return;
        }

        const actionText = isEditMode ? '수정' : '등록';
        if (!window.confirm(`공통코드를 ${actionText}하시겠습니까?`)) return;

        setLoading(true);
        try {
            const userId = getItem<string>('USER_ID');
            if (!userId) {
                showToast(TOAST_MESSAGES.LOGIN_NOT_FOUND, { type: ToastType.ERROR });
                return;
            }

            const groupResponse = await commonCodeApi.saveCommonCodeGroup({
                userId,
                grpCode: formData.grpCode,
                grpName: formData.grpName,
                grpVal: formData.grpVal || undefined,
                seqNo: formData.seqNo,
            });

            if (!groupResponse.success) {
                showToast(groupResponse.message || TOAST_MESSAGES.GROUP_SAVE_FAIL, { type: ToastType.ERROR });
                return;
            }

            const codeResponses = await Promise.all(
                commonCodes.map(code => commonCodeApi.saveCommonCode({
                    userId,
                    grpCode: formData.grpCode,
                    comCode: code.comCode,
                    comCdName: code.comCdName,
                    comSeqno: code.comSeqno.trim() || undefined,
                    seqNo: code.seqNo,
                    useYn: code.useYn,
                    val1: code.val1.trim() || undefined,
                    val2: code.val2.trim() || undefined,
                    val3: code.val3.trim() || undefined,
                    val4: code.val4.trim() || undefined,
                    val5: code.val5.trim() || undefined,
                }))
            );

            const allSuccess = codeResponses.every(res => res.success);
            const groupHasChanges = groupResponse.hasChanges !== false;
            const codeHasChanges = codeResponses.some(res => res.hasChanges !== false);

            if (!groupHasChanges && !codeHasChanges) {
                showToast(TOAST_MESSAGES.NO_CHANGES, { type: ToastType.WARNING });
                return;
            }

            if (allSuccess) {
                showToast(TOAST_MESSAGES.SAVE_SUCCESS(isEditMode), { type: ToastType.SUCCESS });
                window.dispatchEvent(new CustomEvent(DASHBOARD_EVENTS.COMMON_CODE_REFRESH));
                window.dispatchEvent(new CustomEvent(DASHBOARD_EVENTS.NAVIGATE, {
                    detail: { page: SYSTEM_TAB_IDS.COMMON_CODE },
                }));
            } else {
                const failedRes = codeResponses.find(res => !res.success);
                showToast(failedRes?.message || TOAST_MESSAGES.SAVE_FAIL(isEditMode), { type: ToastType.ERROR });
            }
        } catch {
            showToast(TOAST_MESSAGES.SAVE_FAIL(isEditMode), { type: ToastType.ERROR });
        } finally {
            setLoading(false);
        }
    }, [formData, commonCodes, isEditMode, showToast]);

    const handleCancel = useCallback(() => {
        window.dispatchEvent(new CustomEvent(DASHBOARD_EVENTS.CLOSE_TAB, {
            detail: { tabId: SYSTEM_TAB_IDS.COMMON_CODE_ADD },
        }));
        window.dispatchEvent(new CustomEvent(DASHBOARD_EVENTS.NAVIGATE, {
            detail: { page: SYSTEM_TAB_IDS.COMMON_CODE },
        }));
    }, []);

    return {
        grpCode,
        formData,
        commonCodes,
        loading,
        isEditMode,
        handleGroupChange,
        handleCommonCodeChange,
        handleAddCommonCode,
        handleRemoveCommonCode,
        handleRemoveValueField,
        handleAddValueField,
        handleReset,
        handleSubmit,
        handleCancel,
    };
};
